import { Base64 } from 'js-base64';
import DOMPurify from 'dompurify';
import Config from '../config/config';

const randomSuffixes = ['x', 'pro', 'dev', '01', '99', 'alpha', 'beta', 'star'];

class StringHelper { 

    static base64Decode(input) {
        return Base64.decode(input);
    }

    static base64Encode(input) {
        return Base64.encode(input);
    }

    static formatUsername(input) {
        if (!input) return "";
        return `@${input}`
    }

    static getFullName(firstName, lastName) {
      var output = firstName;
      if (lastName) {
        output = `${output} ${lastName}`
      }
      return output
    }

    static generateUsername(firstName, lastName) {
      const lowerFirst = firstName.toLowerCase();
      const lowerLast = lastName.toLowerCase();
    
      const patterns = [
        () => `${lowerFirst}${lowerLast}`,
        () => `${lowerLast}${lowerFirst}`,
        () => `${lowerFirst}_${lowerLast}`,
        () => `${lowerFirst}${Math.floor(Math.random() * 100)}`,
        () => `${lowerLast}${Math.floor(Math.random() * 100)}`,
        () => `${lowerFirst}_${randomSuffixes[Math.floor(Math.random() * randomSuffixes.length)]}`,
      ];
    
      const pattern = patterns[Math.floor(Math.random() * patterns.length)];
      return pattern()
        .replace(/[^a-zA-Z0-9_.-]/g, '')
        .replace(/\.{2,}/g, '.') // Replace multiple dots with one
        .replace(/^\.|\.$/g, ''); 
    }

    static isQuillDeltaObject(input) {
      let regExp = /^\{"ops":/;
      return (typeof input === "object" && input["ops"]) || (typeof input === "string" && regExp.test(input));
    }

    static isHtml(input) {
      let regExp = /(<([^>]+)>)/i;
      return typeof input === "string" && regExp.test(input);
    }

    static addProtocolUrl(input) {
      const doesNotStartWithHttp =
              input &&
              !(
                input.startsWith('http://') ||
                input.startsWith('https://')
              );

      if (doesNotStartWithHttp) {
        return `https://${input}`;
      }
      return input;
    }

    static isValidPhoneNumber(input) {
      return /^\+?\d{6,15}$/.test(input);
    }

    static isValidURL(input) {
      var pattern = new RegExp('^((https?:)?\\/\\/)?'+ // protocol
          '(?:\\S+(?::\\S*)?@)?' + // authentication
          '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|'+ // domain name
          '((\\d{1,3}\\.){3}\\d{1,3}))'+ // OR ip (v4) address
          '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*'+ // port and path
          '(\\?[;&a-z\\d%_.~+=-]*)?'+ // query string
          '(\\#[-a-z\\d_]*)?$','i'); // fragment locater
      
      if (!pattern.test(input)) {
          return false;
      } else {
          return true;
      }
    }

    static isEmail(input) {
      return typeof input === "string" && Config.model.validation.email.regExp.test(input);
    }

    static toNumber(input) {
      if (typeof input === "number") {
        return input;
      } else if(typeof input === "string" && input.match(/^-?\d+$/)){
        //valid integer (positive or negative)
        return parseInt(input, 10);
      } else if(typeof input === "string" && input.match(/^-?\d*\.\d+$/)){
        //valid float
        return parseFloat(input);
      } else {
        return undefined;
      }
    }

    static isValidJson(str) {
      try {
        JSON.parse(str);
        return true;
      } catch (e) {
        return false;
      }
    }

    static objectToString(object) {
      return JSON.stringify(object);
    }

    static base64DecodeAndSanitizeHtml(input) {
      return DOMPurify.sanitize(StringHelper.base64Decode(input), { ALLOWED_ATTR: ['href', 'target', 'rel'] });
    }

    // Decodes a base64-encoded rich-text output (e.g. a description-template block's
    // output slot): when the decoded string is Quill Delta JSON, parses it into the
    // actual Delta object ReactQuill's `value` prop needs to render it as rich text
    // rather than literal JSON text — otherwise returns the decoded string as-is. Never
    // throws: falsy/invalid base64 or unparseable JSON fall back to "" / the raw decoded
    // string respectively.
    static base64DecodeQuillDelta(input) {
      if (!input) return "";
      let decoded;
      try {
        decoded = StringHelper.base64Decode(input);
      } catch (err) {
        return "";
      }
      if (typeof decoded === "string" && StringHelper.isQuillDeltaObject(decoded)) {
        try {
          return JSON.parse(decoded);
        } catch (err) {
          return decoded;
        }
      }
      return decoded;
    }

    // Same decode as base64DecodeQuillDelta, but flattened to plain text (Quill Delta
    // formatting/embeds stripped) — for contexts that can only show plain text, like a
    // rich text editor's `placeholder` (Quill can't render rich formatting there).
    static base64DecodeQuillDeltaToPlainText(input) {
      const decoded = StringHelper.base64DecodeQuillDelta(input);
      if (!decoded) return "";
      if (typeof decoded === "string") return decoded;
      const ops = Array.isArray(decoded.ops) ? decoded.ops : [];
      return ops
        .map(op => (typeof op.insert === "string" ? op.insert : ""))
        .join("")
        .replace(/\s+/g, " ")
        .trim();
    }

    static extractTextFromHTML(htmlString) {
      const tmp = document.createElement("div");
      tmp.innerHTML = htmlString;
      return tmp.textContent || tmp.innerText || "";
    }

    static injectClassToFirstParagraph(htmlString, className) {
      let injected = false;

      return htmlString.replace(/<p(\s[^>]*)?>/i, (match, attrs) => {
        if (injected) return match; // Only modify the first <p>
        injected = true;

        if (!attrs) {
          // <p> with no attributes
          return `<p class="${className}">`;
        }

        if (/class\s*=/i.test(attrs)) {
          // Already has a class attribute — append to it
          return `<p${attrs.replace(/class\s*=\s*["']([^"']*)["']/i,
            (_, existing) => `class="${existing} ${className}"`
          )}>`;
        }

        // Has other attributes but no class
        return `<p class="${className}"${attrs}>`;
      });
    }

    static buildWhatsAppUrl(phone, message) {
      if (!phone) return '';
      const cleanPhone = phone.replace(/\s/g, '');
      const base = `https://wa.me/${cleanPhone.startsWith('+') ? cleanPhone.slice(1) : cleanPhone}`;
      return message ? `${base}?text=${encodeURIComponent(message)}` : base;
    }

    static buildTelegramUrl(phone, message) {
      if (!phone) return '';
      const cleanPhone = phone.replace(/\s/g, '');
      const pathPhone = cleanPhone.startsWith('+') ? cleanPhone.slice(1) : cleanPhone;
      const base = `https://t.me/+${pathPhone}`;
      return message ? `${base}?text=${encodeURIComponent(message)}` : base;
    }

    static parseMessagingUrl(url) {
      const empty = { type: 'regular', phone: '', message: '' };
      if (!url) return empty;
      try {
        // Encode spaces and non-ASCII chars without touching already-encoded % sequences
        const sanitized = Array.from(url).map(c =>
          (c === ' ' || c.charCodeAt(0) > 127) ? encodeURIComponent(c) : c
        ).join('');
        const parsed = new URL(sanitized);
        if (parsed.hostname === 'wa.me') {
          const phone = '+' + parsed.pathname.replace(/^\//, '');
          const message = parsed.searchParams.get('text') || '';
          return { type: 'whatsapp', phone, message };
        }
        if (parsed.hostname === 't.me') {
          const raw = parsed.pathname.replace(/^\//, '');
          const phone = raw.startsWith('+') ? raw : '+' + raw;
          const message = parsed.searchParams.get('text') || '';
          return { type: 'telegram', phone, message };
        }
      } catch {
        // fall through to regular
      }
      return empty;
    }

    static buildParameterInsertion(param, charBefore, charAfter) {
      const needsSpaceBefore = charBefore !== '' && charBefore !== ' ' && charBefore !== '\n';
      const needsSpaceAfter = charAfter !== '' && charAfter !== ' ' && charAfter !== '.' && charAfter !== '\n';
      let text = `{{${param}}}`;
      if (needsSpaceBefore) text = ' ' + text;
      if (needsSpaceAfter) text = text + ' ';
      return text;
    }
}

export default StringHelper;